import classNames from 'classnames';
import capitalize from 'lodash/capitalize';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { RRule } from 'rrule';
import ArchivedItemAction from '@/modules/admin-dashboard/components/ArchivedItemAction.tsx';
import UsersAvatarsSet from '@/modules/admin-dashboard/components/UsersAvatarsSet.tsx';
import TaskItemResultInfo from '@/modules/admin-dashboard/components/items/TaskItemResultInfo.tsx';
import { priorityOptions } from '@/modules/admin-dashboard/components/modals/TaskModal.tsx';
import {
  useCreateTaskMutation,
  useDeleteTaskMutation,
  useRestoreTaskMutation,
} from '@/modules/admin-dashboard/queries/tasks.ts';
import { adminDashboardPaths } from '@/modules/admin-dashboard/routes.tsx';
import {
  ConfirmModal,
  DisclosureListItem,
  DropdownMenu,
  ModalProviderContext,
  TextWithShortLinks,
} from 'ui';
import { ChevronDownIcon, DotsHorizontalIcon } from 'ui/icons';
import {
  TaskListWithRelationsResponse,
  TaskListWithRelationsSummaryResponse,
  TaskResultSchema,
  TaskResultSummaryResponse,
  User,
} from 'compliance-api';

interface TaskHistoryItemProps {
  task: TaskListWithRelationsResponse | TaskListWithRelationsSummaryResponse;
  taskIdToOpen?: number | null;
  taskResultIdToOpen?: number | null;
  onOpenFromUrl: () => void;
  filterByName?: string;
}

const TaskHistoryItem = ({
  task,
  taskIdToOpen,
  taskResultIdToOpen,
  onOpenFromUrl,
  filterByName,
}: TaskHistoryItemProps) => {
  const [isUsersOpen, setIsUsersOpen] = useState<boolean>(task.id == taskIdToOpen);
  const { openModal } = useContext(ModalProviderContext);
  const [params] = useSearchParams();
  const disclosureRef = useRef<HTMLDivElement>(null);
  const resultItemRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const prevFilterByNameRef = useRef<string | undefined>();

  useEffect(() => {
    const wasSearching = !!prevFilterByNameRef.current;
    const isSearching = !!filterByName;

    if (isSearching && !wasSearching && !isUsersOpen) {
      disclosureRef.current?.click();
    }
  }, [filterByName, isUsersOpen]);

  useEffect(() => {
    prevFilterByNameRef.current = filterByName;
  });

  useEffect(() => {
    if (taskIdToOpen && task.id == taskIdToOpen) {
      disclosureRef.current?.click();
      if (taskResultIdToOpen) {
        setTimeout(() => {
          resultItemRefs.current[taskResultIdToOpen]?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }, 200);
      }
    }
  }, [taskIdToOpen, taskResultIdToOpen]);

  const { name, task_results, users, deleted_at } = task;

  const { mutateAsync: createTask } = useCreateTaskMutation();
  const { mutateAsync: deleteTask } = useDeleteTaskMutation();

  const handleDuplicateTask = useCallback(async () => {
    const additionalContent = ' (Duplicated)';
    const resolveName = task.name.substring(0, 50 - (additionalContent.length + 1));

    await createTask({ ...task, name: `${resolveName} ${additionalContent}` });
  }, [createTask, task]);

  const taskResultsItems = useMemo(() => {
    const usersById = new Map<number, User>((users || []).map(u => [u.id, u]));

    const resultsWithUsers = (task_results || []).reduce(
      (
        acc: Array<{ user: User; result: TaskResultSchema | TaskResultSummaryResponse }>,
        result,
      ) => {
        const user = usersById.get(result.completed_by_user_id);
        if (user) acc.push({ user, result });
        return acc;
      },
      [],
    );

    const usersWithResults = new Set(resultsWithUsers.map(({ user }) => user.id));
    const usersWithoutResults = (users || []).filter(u => !usersWithResults.has(u.id));

    return [
      ...resultsWithUsers,
      ...usersWithoutResults.map(user => ({ user, result: null as TaskResultSchema | null })),
    ];
  }, [users, task_results]);

  const options = useMemo(
    () => [
      {
        label: 'Duplicate',
        onClick: () =>
          openModal(ConfirmModal, {
            onConfirm: handleDuplicateTask,
            title: (
              <p>
                Are you sure you want to duplicate <span className="text-blue-700">{name}</span> ?
              </p>
            ),
          }),
      },
      {
        label: 'Archive',
        onClick: () =>
          openModal(ConfirmModal, {
            onConfirm: async () => {
              deleteTask(task?.id);
            },
            title: (
              <p>
                Are you sure you want to archive <span className="text-blue-700">{name}</span>?
              </p>
            ),
          }),
      },
    ],
    [deleteTask, handleDuplicateTask, name, openModal, task?.id],
  );

  const bottomCustomContent = !!taskResultsItems.length && (
    <div className="w-full">
      <div className="border-b ml-[-24px] mr-[-24px]" />
      {taskResultsItems.map(({ user, result }) => {
        const open = result?.id === taskResultIdToOpen;
        return (
          <div
            key={result?.id || user.id}
            ref={el => result?.id && (resultItemRefs.current[result.id] = el)}
          >
            <TaskItemResultInfo
              user={user}
              task={task}
              result={result}
              openFromUrl={open}
              onOpenFromUrl={onOpenFromUrl}
            />
          </div>
        );
      })}
    </div>
  );

  return (
    <DisclosureListItem
      ref={disclosureRef}
      leftMainString={<div className="break-all">{task.name}</div>}
      contentGridColumns="minmax(auto, 30%) 1fr min-content"
      classes={{
        container: classNames({ 'bg-gray-100': deleted_at }),
        openerArrow: 'hidden',
        rightContent: classNames('!border-none', { 'w-[260px]': !deleted_at }),
      }}
      leftSubduedContentSet={[
        task.description ? <TextWithShortLinks text={task.description} /> : '...',
      ]}
      onOpen={() => setIsUsersOpen(true)}
      onClose={() => setIsUsersOpen(false)}
      bottomCustomContent={bottomCustomContent}
      rightLeftContent={
        !deleted_at && (
          <div className="grid grid-cols-1 w-full">
            {task.users?.length ? (
              <div className="gap-4 items-center hidden lg:flex justify-between w-full">
                <div className="flex items-center gap-4 flex-1 justify-between md:pl-[10%]">
                  <div className="flex flex-col w-[180px]">
                    <div className="text-[12px] text-gray-500 hidden sm:inline">Form</div>
                    <Link
                      to={{
                        pathname: adminDashboardPaths.dynamic.form(task.form_id),
                        search: params.toString(),
                      }}
                      replace={false}
                      className="hover:underline truncate"
                    >
                      {task.form_name}
                    </Link>
                  </div>
                  <div className="flex flex-col w-[220px]">
                    <div className="text-[12px] text-gray-500 hidden sm:inline">Recurrence</div>
                    <div className="truncate">
                      {task?.recurrency_rule
                        ? capitalize(RRule.fromString(task?.recurrency_rule).toText())
                        : '-'}
                    </div>
                  </div>
                  <div className="flex flex-col sm:w-[80px]">
                    <div className="text-[12px] text-gray-500 hidden sm:inline">Priority</div>
                    <div className="sm:[&>div>span:nth-child(2)]:inline [&>div>span:nth-child(2)]:hidden">
                      {priorityOptions.find(item => item.value == task?.priority)?.label}
                    </div>
                  </div>
                  <UsersAvatarsSet users={task?.users} />
                </div>
                <img
                  src={ChevronDownIcon}
                  alt="open"
                  width={24}
                  height={24}
                  className={classNames({ 'rotate-180': isUsersOpen })}
                />
              </div>
            ) : (
              <div className="w-[140px] hidden lg:flex" />
            )}
          </div>
        )
      }
      rightAdditionalContent={
        deleted_at ? (
          <ArchivedItemAction id={task.id} mutation={useRestoreTaskMutation} />
        ) : (
          <DropdownMenu
            autoPlace
            items={options}
            button={
              <button className="flex items-center justify-center">
                <img src={DotsHorizontalIcon} alt="menu" />
              </button>
            }
            className="w-[180px] divide-white"
          />
        )
      }
    />
  );
};

export default TaskHistoryItem;
